import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { GripVertical, X, Hand } from "lucide-react";
import api from "@/lib/api";
import { TeamLogo } from "./BracketView";

function Chip({ name, logo, selected, onPick, onDragStart, testid, onClear }) {
  return (
    <motion.div layout draggable onDragStart={onDragStart} onClick={onPick} data-testid={testid}
      className={`group flex items-center gap-2 pl-1.5 pr-2 py-1.5 rounded-lg cursor-grab active:cursor-grabbing select-none border transition-colors ${selected ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/15" : "border-white/10 bg-white/[0.06] hover:border-white/25"}`}>
      <GripVertical size={13} className="text-white/30 shrink-0" />
      <TeamLogo team={name} logos={{ [name]: logo }} size="w-5 h-5" />
      <span className="text-sm font-semibold text-white truncate">{name}</span>
      {onClear && <button onClick={(e) => { e.stopPropagation(); onClear(); }} className="ml-auto p-0.5 text-white/30 hover:text-red-400" title="Remove from slot"><X size={12} /></button>}
    </motion.div>
  );
}

export default function SlotBoard({ t, setT }) {
  const slots = t.bracket?.slots || [];
  const teams = (t.participants || []).filter((p) => p.status === "confirmed");
  const logo = Object.fromEntries(teams.map((p) => [p.name, p.logo]));
  const placed = new Set(slots.map((s) => s.team).filter(Boolean));
  const unplaced = teams.filter((p) => !placed.has(p.name));
  const [picked, setPicked] = useState(null);
  const [over, setOver] = useState(null);
  const [busy, setBusy] = useState(false);

  const put = (slot, team) => api.put(`/manage/tournaments/${t.id}/slots`, { slot, team }).then((r) => r.data);
  const move = async (team, fromSlot, toSlot) => {
    if (busy || fromSlot === toSlot) return;
    setBusy(true);
    try {
      let data;
      const target = slots.find((s) => s.slot === toSlot);
      const displaced = target?.team;
      if (fromSlot) data = await put(fromSlot, null);
      if (toSlot) data = await put(toSlot, team);
      if (toSlot && fromSlot && displaced) data = await put(fromSlot, displaced);
      if (data) setT(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not move team"); }
    finally { setBusy(false); setPicked(null); setOver(null); }
  };
  const drag = (team, from) => (e) => { e.dataTransfer.setData("text/plain", JSON.stringify({ team, from })); e.dataTransfer.effectAllowed = "move"; };
  const drop = (toSlot) => (e) => {
    e.preventDefault();
    try { const { team, from } = JSON.parse(e.dataTransfer.getData("text/plain")); move(team, from, toSlot); } catch { setOver(null); }
  };
  const tapSlot = (s) => { if (picked) move(picked.team, picked.from, s.slot); else if (s.team) setPicked({ team: s.team, from: s.slot }); };

  if (!slots.length) return null;
  const groups = [...new Set(slots.map((s) => s.group || ""))];
  return (
    <div className={`card-surface rounded-2xl p-5 ${busy ? "opacity-70 pointer-events-none" : ""}`} data-testid="slots-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div><h3 className="font-bold text-white">Team placement</h3><p className="text-xs text-white/40 flex items-center gap-1.5"><Hand size={12} /> Drag teams onto slots — or tap a team, then tap a slot. Drop on a filled slot to swap.</p></div>
        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white/5 text-white/60" data-testid="unplaced-count">{unplaced.length ? `${unplaced.length} registered team(s) not placed yet` : "All registered teams are placed"}</span>
      </div>
      <div onDragOver={(e) => e.preventDefault()} onDrop={drop(null)} className="rounded-xl border border-dashed border-white/15 bg-black/20 p-3 mb-5 min-h-[56px]" data-testid="unplaced-pool">
        <div className="eyebrow text-white/40 mb-2">Registered teams</div>
        <div className="flex flex-wrap gap-2">
          {unplaced.map((p) => <Chip key={p.id} name={p.name} logo={p.logo} selected={picked?.team === p.name} onDragStart={drag(p.name, null)} onPick={() => setPicked(picked?.team === p.name ? null : { team: p.name, from: null })} testid={`pool-team-${p.id}`} />)}
          {!unplaced.length && <span className="text-xs text-white/30">{teams.length ? "Drop a team here to remove it from the bracket." : "No confirmed teams yet — share the registration link."}</span>}
        </div>
      </div>
      <div className="space-y-5">
        {groups.map((g) => (
          <div key={g}>
            {g && <div className="eyebrow text-white/40 mb-2">Group {g}</div>}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {slots.filter((s) => (s.group || "") === g).map((s) => (
                <div key={s.slot} onDragOver={(e) => { e.preventDefault(); setOver(s.slot); }} onDragLeave={() => setOver(null)} onDrop={drop(s.slot)} onClick={() => tapSlot(s)}
                  data-testid={`slot-${s.slot.replace(" ", "-")}`}
                  className={`relative rounded-xl border p-2 min-h-[52px] flex items-center gap-2 transition-colors cursor-pointer ${over === s.slot ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15" : s.team ? "border-white/10 bg-white/[0.03]" : "border-dashed border-white/15 bg-black/20 hover:border-white/30"}`}>
                  <span className="text-[10px] font-bold text-white/30 w-10 shrink-0 text-center">{s.slot.replace("Slot ", "#")}</span>
                  {s.team ? <div className="flex-1 min-w-0"><Chip name={s.team} logo={logo[s.team]} selected={picked?.team === s.team} onDragStart={drag(s.team, s.slot)} onPick={(e) => { e?.stopPropagation?.(); setPicked(picked?.team === s.team ? null : { team: s.team, from: s.slot }); }} onClear={() => move(s.team, s.slot, null)} testid={`slot-team-${s.slot.replace(" ", "-")}`} /></div>
                    : <span className="text-xs text-white/30">{picked ? "Tap to place here" : "Empty"}</span>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
